import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgModule,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
    selector: 'ts-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [style({ opacity: 0 }), animate('150ms ease-in', style({ opacity: 1 }))]),
            transition(':leave', [animate('150ms ease-out', style({ opacity: 0 }))]),
        ]),
    ],
    standalone: false
})
export class SearchBarComponent implements OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('tableSearchBar', { read: ElementRef })
  private searchBar: ElementRef<HTMLInputElement>;

  //fix: must be an input
  public searchBy = { term: 'name' };

  private _destroy$ = new Subject<void>();

  @Optional() @Input() searchValue = '';

  @Output() searchChanged = new EventEmitter<string>();

  ngOnChanges(changes: SimpleChanges) {
    if (changes['searchValue'] && this.searchBar) {
      const isInputFocused = document.activeElement === this.searchBar.nativeElement;
      if (!isInputFocused) {
        this.searchBar.nativeElement.value = this.searchValue ?? '';
      }
    }
  }

  ngAfterViewInit() {
    this.searchBar.nativeElement.value = this.searchValue ?? '';
    fromEvent(this.searchBar.nativeElement, 'keyup')
      .pipe(
        map(() => this.searchBar.nativeElement.value),
        distinctUntilChanged(),
        debounceTime(300),
        takeUntil(this._destroy$)
      )
      .subscribe((term) => {
        this.searchValue = term;
        this.searchChanged.emit(term);
      });
  }

  clearSearch(): void {
    this.searchValue = '';
    this.searchBar.nativeElement.value = '';
    this.searchChanged.emit('');
  }

  ngOnDestroy() {
    this._destroy$.next();
  }
}

@NgModule({
  imports: [CommonModule, TranslateModule, MatFormFieldModule, MatInputModule, MatButtonModule],
  declarations: [SearchBarComponent],
  exports: [SearchBarComponent],
})
export class SearchBarComponentModule {}
